import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Address, StartingPoint } from '../types/index';
import { calculateOptimalRoute } from '../services/routeService';
import { Check, X, MapPin, Navigation, Locate, Pentagon } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapComponentProps {
  addresses: Address[];
  onAddressClick: (address: Address) => void;
  onPolygonComplete: (selectedAddresses: Address[]) => void;
  startingPoint: StartingPoint;
  onRouteCreated: (route: any) => void;
  shouldCreateRoute: boolean;
  onRouteCreationComplete: () => void;
  onClearRoute: () => void;
  onClearPoints: () => void;
  shouldClearRoute: boolean;
  shouldClearPoints: boolean;
  shouldRedrawMap?: boolean; // trigger map redraw when layout changes
  shouldStartPolygonDraw?: boolean; // trigger polygon drawing from external controls
  onPolygonDrawingStateChange?: (isDrawing: boolean) => void; // callback for drawing state changes
}

// Custom icons for different marker types
const createCustomIcon = (color: string, size: 'small' | 'medium' | 'large' = 'medium') => {
  const sizes = {
    small: [20, 20],
    medium: [25, 25], 
    large: [30, 30]
  };
  
  const [width, height] = sizes[size];
  
  return L.divIcon({
    html: `<div style="
      width: ${width}px;
      height: ${width}px;
      background-color: ${color};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    "></div>`,
    className: 'custom-marker',
    iconSize: [width, height],
    iconAnchor: [width/2, height/2]
  });
};

const redIcon = createCustomIcon('#dc2626');
const blueIcon = createCustomIcon('#2563eb');
const greenIcon = createCustomIcon('#16a34a');
const startIcon = createCustomIcon('#dc2626', 'large');
const currentLocationIcon = createCustomIcon('#8b5cf6', 'large'); // Purple for current location

// Component to handle map resizing when layout changes
const MapResizeHandler: React.FC<{ onResize: boolean }> = ({ onResize }) => {
  const map = useMap();
  
  useEffect(() => {
    if (onResize) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        map.invalidateSize();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [onResize, map]);
  
  return null;
};

// Component to handle user location and recentering
const LocationControl: React.FC = () => {
  const map = useMap();
  const [isLocating, setIsLocating] = useState(false);
  const [currentLocationMarker, setCurrentLocationMarker] = useState<L.Marker | null>(null);

  const handleRecenter = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Center the map on current location
        map.setView([latitude, longitude], 15);
        
        // Remove previous location marker if it exists
        if (currentLocationMarker) {
          map.removeLayer(currentLocationMarker);
        }
        
        // Add new location marker
        const marker = L.marker([latitude, longitude], {
          icon: currentLocationIcon
        }).addTo(map);
        
        marker.bindPopup(`
          <div class="p-2 min-w-[180px]">
            <h3 class="font-semibold text-purple-600 mb-1 text-sm">📍 Your Location</h3>
            <div class="text-xs text-gray-500">
              Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}
            </div>
            <div class="text-xs text-gray-400 mt-1">
              Accuracy: ±${position.coords.accuracy?.toFixed(0) || 'N/A'}m
            </div>
          </div>
        `);
        
        setCurrentLocationMarker(marker);
        setIsLocating(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to get your location.';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        
        alert(errorMessage);
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }, [map, currentLocationMarker]);

  return (
    <div className="absolute top-20 right-4 z-[1000]">
      <button
        onClick={handleRecenter}
        disabled={isLocating}
        className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-700 p-2 rounded-lg shadow-lg border border-gray-200 transition-colors"
        title="Center map on your location"
      >
        {isLocating ? (
          <div className="animate-spin">
            <Locate size={20} />
          </div>
        ) : (
          <Locate size={20} />
        )}
      </button>
    </div>
  );
};

// Component to ensure map always centers on starting point
const StartingPointCentering: React.FC<{
  startingPoint: StartingPoint;
  addresses: Address[];
}> = ({ startingPoint, addresses }) => {
  const map = useMap();
  const prevStartingPoint = useRef<StartingPoint | null>(null);
  const prevAddressCount = useRef(0);

  useEffect(() => {
    console.log('StartingPointCentering effect triggered', { 
      startingPoint, 
      addressCount: addresses.length,
      prevAddressCount: prevAddressCount.current 
    });

    // Check if starting point is valid and has coordinates
    if (startingPoint.isValid && startingPoint.latitude && startingPoint.longitude) {
      const startingLatLng = L.latLng(startingPoint.latitude, startingPoint.longitude);
      
      // Check if starting point changed
      const startingPointChanged = !prevStartingPoint.current || 
        prevStartingPoint.current.latitude !== startingPoint.latitude ||
        prevStartingPoint.current.longitude !== startingPoint.longitude;
      
      // Check if new addresses were added
      const validAddresses = addresses.filter(addr => addr.latitude && addr.longitude);
      const addressesAdded = validAddresses.length > prevAddressCount.current;
      
      if (startingPointChanged || addressesAdded) {
        console.log('Centering map on starting point', { 
          lat: startingPoint.latitude, 
          lng: startingPoint.longitude,
          addressCount: validAddresses.length 
        });
        
        if (validAddresses.length === 0) {
          // No addresses yet, just center on starting point with high zoom
          map.setView(startingLatLng, 16);
        } else {
          // Calculate the maximum distance from starting point to any address
          let maxDistance = 0;
          validAddresses.forEach(addr => {
            const addressLatLng = L.latLng(addr.latitude!, addr.longitude!);
            const distance = startingLatLng.distanceTo(addressLatLng);
            maxDistance = Math.max(maxDistance, distance);
          });
          
          // Determine zoom level based on spread
          let zoomLevel = 16; // Default high zoom
          
          if (maxDistance > 50000) {      // > 50km - very spread out
            zoomLevel = 10;
          } else if (maxDistance > 25000) { // > 25km - spread out
            zoomLevel = 11;
          } else if (maxDistance > 10000) { // > 10km - moderately spread
            zoomLevel = 12;
          } else if (maxDistance > 5000) {  // > 5km - somewhat spread
            zoomLevel = 13;
          } else if (maxDistance > 2000) {  // > 2km - close together
            zoomLevel = 14;
          } else if (maxDistance > 500) {   // > 500m - very close
            zoomLevel = 15;
          }
          
          console.log('Setting zoom level', { maxDistance, zoomLevel });
          
          // Always center on starting point with calculated zoom
          map.setView(startingLatLng, zoomLevel);
        }
        
        prevStartingPoint.current = { ...startingPoint };
        prevAddressCount.current = validAddresses.length;
      }
    } else {
      console.log('Starting point not valid', startingPoint);
    }
  }, [startingPoint, addresses, map]);

  return null;
};

// Component to handle map events and route creation
const MapEvents: React.FC<{
  onRouteCreated: (route: any) => void;
  shouldCreateRoute: boolean;
  onRouteCreationComplete: () => void;
  addresses: Address[];
  startingPoint: StartingPoint;
}> = ({ onRouteCreated, shouldCreateRoute, onRouteCreationComplete, addresses, startingPoint }) => {
  const map = useMap();

  useEffect(() => {
    if (!shouldCreateRoute) return;

    const createRoute = async () => {
      try {
        const selectedAddresses = addresses.filter(addr => 
          addr.isSelected && addr.latitude && addr.longitude
        );
        
        if (selectedAddresses.length === 0 || !startingPoint.latitude || !startingPoint.longitude) {
          throw new Error('No valid addresses or starting point');
        }

        const waypoints = selectedAddresses.map(addr => ({
          lat: addr.latitude!,
          lng: addr.longitude!
        }));

        const route = await calculateOptimalRoute(
          { lat: startingPoint.latitude, lng: startingPoint.longitude },
          waypoints
        );

        // Draw route on map
        if (route.coordinates && route.coordinates.length > 0) {
          const polyline = L.polyline(route.coordinates, {
            color: '#ef4444',
            weight: 5,
            opacity: 0.8,
            lineCap: 'round',
            lineJoin: 'round'
          }).addTo(map);

          // Add route markers for waypoints
          route.coordinates.forEach((coord, index) => {
            if (index % Math.floor(route.coordinates.length / 10) === 0) { // Show every 10th point
              L.circleMarker(coord, {
                radius: 2,
                fillColor: '#ef4444',
                color: '#ffffff',
                weight: 1,
                opacity: 0.8,
                fillOpacity: 0.8
              }).addTo(map);
            }
          });

          // Fit bounds to show the entire route
          map.fitBounds(polyline.getBounds(), { padding: [20, 20] });

          // Show route info using a custom control
          const RouteInfoControl = L.Control.extend({
            options: {
              position: 'bottomright'
            },
            onAdd: function() {
              const div = L.DomUtil.create('div', 'route-info');
              div.innerHTML = `
                <div style="background: white; padding: 8px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); font-size: 12px;">
                  <strong>Route Info:</strong><br>
                  Distance: ${(route.distance / 1000).toFixed(1)} km<br>
                  Duration: ${Math.round(route.duration / 60)} min<br>
                  <div style="margin-top: 4px; font-size: 10px; color: #666;">
                    🚗 Driving directions
                  </div>
                </div>
              `;
              return div;
            }
          });
          
          const routeInfo = new RouteInfoControl();
          routeInfo.addTo(map);

          onRouteCreated({
            ...route,
            polyline: polyline,
            routeInfo: routeInfo
          });
        }
      } catch (error) {
        console.error('Error creating route:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to create route. Please try again.';
        alert(errorMessage);
      } finally {
        onRouteCreationComplete();
      }
    };

    createRoute();
  }, [shouldCreateRoute, addresses, startingPoint, map, onRouteCreated, onRouteCreationComplete]);

  return null;
};

// Component to handle polygon drawing
const PolygonDrawer: React.FC<{
  onPolygonComplete: (selectedAddresses: Address[]) => void;
  addresses: Address[];
  shouldStartPolygonDraw?: boolean;
  onPolygonDrawingStateChange?: (isDrawing: boolean) => void;
}> = ({ onPolygonComplete, addresses, shouldStartPolygonDraw, onPolygonDrawingStateChange }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState<L.LatLng[]>([]);
  const [markers, setMarkers] = useState<L.Layer[]>([]);
  const [lines, setLines] = useState<L.Polyline[]>([]);
  const [showControls, setShowControls] = useState(false);

  const map = useMap();

  const startDrawing = useCallback(() => {
    setIsDrawing(true);
    setPoints([]);
    setMarkers([]);
    setLines([]);
    setShowControls(false);
    onPolygonDrawingStateChange?.(true);
  }, [onPolygonDrawingStateChange]);

  // Handle external trigger for polygon drawing
  useEffect(() => {
    if (shouldStartPolygonDraw && !isDrawing) {
      startDrawing();
    }
  }, [shouldStartPolygonDraw, isDrawing, startDrawing]);

  const finishDrawing = useCallback(() => {
    if (points.length < 3) {
      alert('Please click at least 3 points to create a polygon.');
      return;
    }

    // Check which addresses are inside the polygon
    const addressesInPolygon = addresses.filter(address => {
      if (!address.latitude || !address.longitude) return false;
      const point = L.latLng(address.latitude, address.longitude);
      return isPointInPolygon(point, points);
    });

    onPolygonComplete(addressesInPolygon);
    
    // Clean up
    markers.forEach(marker => map.removeLayer(marker));
    lines.forEach(line => map.removeLayer(line));
    
    setIsDrawing(false);
    setPoints([]);
    setMarkers([]);
    setLines([]);
    setShowControls(false);
    onPolygonDrawingStateChange?.(false);
  }, [points, addresses, onPolygonComplete, map, markers, lines, onPolygonDrawingStateChange]);

  const isPointInPolygon = (point: L.LatLng, polygon: L.LatLng[]): boolean => {
    const x = point.lat;
    const y = point.lng;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat;
      const yi = polygon[i].lng;
      const xj = polygon[j].lat;
      const yj = polygon[j].lng;

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  };

  const handleAccept = useCallback(() => {
    finishDrawing();
  }, [finishDrawing]);

  const handleCancel = useCallback(() => {
    // Clean up
    markers.forEach(marker => map.removeLayer(marker));
    lines.forEach(line => map.removeLayer(line));
    
    setIsDrawing(false);
    setPoints([]);
    setMarkers([]);
    setLines([]);
    setShowControls(false);
    onPolygonDrawingStateChange?.(false);
  }, [map, markers, lines, onPolygonDrawingStateChange]);

  useMapEvents({
    click: (e) => {
      if (isDrawing) {
        const newPoints = [...points, e.latlng];
        setPoints(newPoints);
        
        // Show controls after first point is drawn
        if (newPoints.length === 1) {
          setShowControls(true);
        }
        
        // Add a point marker
        const marker = L.circleMarker(e.latlng, {
          radius: 4,
          fillColor: '#3b82f6',
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        }).addTo(map);
        
        setMarkers(prev => [...prev, marker]);
        
        // Draw line to previous point
        if (newPoints.length > 1) {
          const line = L.polyline([newPoints[newPoints.length - 2], e.latlng], {
            color: '#3b82f6',
            weight: 2,
            opacity: 0.8,
            dashArray: '5, 5'
          }).addTo(map);
          
          setLines(prev => [...prev, line]);
        }
        
        // Draw line back to first point if we have 3+ points
        if (newPoints.length >= 3) {
          const closingLine = L.polyline([e.latlng, newPoints[0]], {
            color: '#3b82f6',
            weight: 2,
            opacity: 0.5,
            dashArray: '5, 5'
          }).addTo(map);
          
          setLines(prev => [...prev, closingLine]);
        }
      }
    },
    dblclick: (e) => {
      if (isDrawing) {
        e.originalEvent.preventDefault();
        finishDrawing();
      }
    }
  });

  return (
    <>
      {isDrawing && (
        <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg">
          <div className="px-3 py-2 text-sm text-center">
            <div className="font-medium text-blue-600 mb-1">Drawing Mode</div>
            <div className="text-xs text-gray-600">
              Click to add points<br/>
              Double-click to finish<br/>
              ({points.length} points)
            </div>
          </div>
        </div>
      )}

      {showControls && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg p-2 flex gap-2">
          <button
            onClick={handleAccept}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Check size={14} />
            Accept
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            <X size={14} />
            Cancel
          </button>
        </div>
      )}
    </>
  );
};

const MapComponent: React.FC<MapComponentProps> = ({
  addresses,
  onAddressClick,
  onPolygonComplete,
  startingPoint,
  onRouteCreated,
  shouldCreateRoute,
  onRouteCreationComplete,
  shouldRedrawMap = false,
  shouldStartPolygonDraw = false,
  onPolygonDrawingStateChange,
}) => {
  console.log('MapComponent render', { startingPoint, addressCount: addresses.length });

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={[40.7128, -74.0060]} // Default center, will be overridden by StartingPointCentering
        zoom={12}
        className="h-full w-full"
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Component to ensure map centers on starting point */}
        <StartingPointCentering addresses={addresses} startingPoint={startingPoint} />
        
        {/* Location control for recentering */}
        <LocationControl />
        
        {/* Map resize handler for layout changes */}
        <MapResizeHandler onResize={shouldRedrawMap} />
        
        {/* Address markers */}
        {addresses.filter(addr => addr.latitude && addr.longitude).map(address => {
          let icon = redIcon;
          if (address.isOnRoute) {
            icon = greenIcon;
          } else if (address.isSelected) {
            icon = blueIcon;
          }

          return (
            <Marker
              key={address.id}
              position={[address.latitude!, address.longitude!]}
              icon={icon}
              eventHandlers={{
                click: () => onAddressClick(address)
              }}
            >
              <Popup>
                <div className="p-2 min-w-[180px]">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm">{address.businessName}</h3>
                  <p className="text-xs text-gray-600">{address.address}</p>
                  <div className="text-xs text-gray-500 mt-1">
                    Lat: {address.latitude?.toFixed(4)}, Lng: {address.longitude?.toFixed(4)}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Starting point marker */}
        {startingPoint.isValid && startingPoint.latitude && startingPoint.longitude && (
          <Marker
            position={[startingPoint.latitude, startingPoint.longitude]}
            icon={startIcon}
          >
            <Popup>
              <div className="p-2 min-w-[180px]">
                <h3 className="font-semibold text-red-600 mb-1 text-sm">🚩 Starting Point</h3>
                <p className="text-xs text-gray-600">{startingPoint.address}</p>
                <div className="text-xs text-gray-500 mt-1">
                  Lat: {startingPoint.latitude?.toFixed(4)}, Lng: {startingPoint.longitude?.toFixed(4)}
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Map events handler */}
        <MapEvents
          onRouteCreated={onRouteCreated}
          shouldCreateRoute={shouldCreateRoute}
          onRouteCreationComplete={onRouteCreationComplete}
          addresses={addresses}
          startingPoint={startingPoint}
        />

        {/* Polygon drawer */}
        <PolygonDrawer
          onPolygonComplete={onPolygonComplete}
          addresses={addresses}
          shouldStartPolygonDraw={shouldStartPolygonDraw}
          onPolygonDrawingStateChange={onPolygonDrawingStateChange}
        />
      </MapContainer>

      {/* Route processing loading overlay */}
      {shouldCreateRoute && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
          <div className="bg-white rounded-lg p-6 shadow-xl text-center">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Route</h3>
            <p className="text-sm text-gray-600">
              Processing optimal route through selected addresses...
            </p>
          </div>
        </div>
      )}

      {/* Real-time address counter */}
      <div className="absolute top-20 right-4 z-[1000] bg-white rounded-lg shadow-lg p-2">
        <div className="text-sm text-gray-700">
          <div className="font-medium">📍 Plotted Points</div>
          <div className="text-xs">
            {addresses.filter(addr => addr.latitude && addr.longitude).length} / {addresses.length} addresses
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3">
        <h4 className="text-xs font-semibold text-gray-700 mb-2">Legend</h4>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Selected</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-600 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">On Route</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-purple-600 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Your Location</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;